/**
 * Analytics pieces shared by the browser and the server: ID formats, the consent cookie, the first-party cookies GA4 and
 * Meta set, and the booking-funnel event names. No Node or React imports, so both sides can use it.
 *
 * Two parties track public pages: Villa OS itself (every booking site, the landing page, payment pages) and, when the
 * plan includes OWN_TRACKING, the owner of a booking site (their own site only). Both receive the same events.
 */

/** Set by the consent banner on the guest's browser: "granted" or "denied". */
export const CONSENT_COOKIE = "vos_consent";
export const CONSENT_MAX_AGE_DAYS = 180;
export type ConsentChoice = "granted" | "denied";

/** GA4 measurement IDs and Meta Pixel IDs a page loads. Either may be missing. */
export interface TrackingIds {
  ga4_id: string | null;
  meta_pixel_id: string | null;
}

/** An owner's tracking on their booking site, as public pages receive it (never any secret). */
export type SiteTrackingPublic = TrackingIds;

/**
 * Booking-funnel events, named as GA4's recommended ecommerce events. Meta receives the matching standard event.
 * The paid booking (purchase) is also sent from the server, see src/lib/services/tracking-service.ts.
 */
export type FunnelEvent = "view_item" | "search" | "begin_checkout" | "add_payment_info" | "purchase" | "generate_lead";

export const META_EVENT: Record<FunnelEvent, string> = {
  view_item: "ViewContent",
  search: "Search",
  begin_checkout: "InitiateCheckout",
  add_payment_info: "AddPaymentInfo",
  purchase: "Purchase",
  generate_lead: "Lead",
};

const GA4_ID = /^G-[A-Z0-9]{4,20}$/;
const META_PIXEL_ID = /^\d{8,20}$/;

export function isGa4MeasurementId(value: unknown): value is string {
  return typeof value === "string" && GA4_ID.test(value);
}

export function isMetaPixelId(value: unknown): value is string {
  return typeof value === "string" && META_PIXEL_ID.test(value);
}

/** `g-abc123 ` -> `G-ABC123`; empty -> null. Does not validate. */
export function normalizeGa4Id(raw: string | null | undefined): string | null {
  const v = (raw ?? "").trim().toUpperCase();
  return v || null;
}

/** Pixel IDs are digits; spaces people paste along are dropped. Empty -> null. Does not validate. */
export function normalizeMetaPixelId(raw: string | null | undefined): string | null {
  const v = (raw ?? "").replace(/\s+/g, "");
  return v || null;
}

/** Same event ID in the browser pixel and the Conversions API, so Meta counts the purchase once. */
export function purchaseEventId(bookingReference: string): string {
  return `purchase_${bookingReference}`;
}

// ---------------------------------------------------------------------------------------------
// First-party cookies
// ---------------------------------------------------------------------------------------------

export function parseCookieHeader(header: string | null | undefined): Record<string, string> {
  const out: Record<string, string> = {};
  for (const part of (header ?? "").split(";")) {
    const i = part.indexOf("=");
    if (i <= 0) continue;
    const name = part.slice(0, i).trim();
    if (!name || name in out) continue;
    try {
      out[name] = decodeURIComponent(part.slice(i + 1).trim());
    } catch {
      out[name] = part.slice(i + 1).trim();
    }
  }
  return out;
}

/** GA's client ID from its `_ga` cookie: `GA1.1.1234567890.1700000000` -> `1234567890.1700000000`. */
export function gaClientIdFromCookie(value: string | null | undefined): string | null {
  const m = /^GA\d\.\d+\.(\d+\.\d+)$/.exec((value ?? "").trim());
  return m ? m[1] : null;
}

/** The name of the cookie holding a GA4 property's session: `_ga_<ID without "G-">`. */
export function gaSessionCookieName(measurementId: string): string {
  return `_ga_${measurementId.replace(/^G-/, "")}`;
}

/** The session ID from a `_ga_<ID>` cookie, in either format: `GS1.1.1700000000.3.1...` or `GS2.1.s1700000000$o3$g1...`. */
export function gaSessionIdFromCookie(value: string | null | undefined): string | null {
  const v = (value ?? "").trim();
  const gs2 = /^GS2\.\d+\.s(\d+)/.exec(v);
  if (gs2) return gs2[1];
  const gs1 = /^GS1\.\d+\.(\d+)\./.exec(v);
  return gs1 ? gs1[1] : null;
}

/** Meta's browser ID (`_fbp`) and click ID (`_fbc`) cookies look like `fb.1.<time>.<value>`. */
export function isMetaCookieValue(value: string | null | undefined): value is string {
  return typeof value === "string" && /^fb\.\d\.\d+\.[\w.-]{1,500}$/.test(value);
}
